import { appSessionRevision } from '@/services/appSession';
import { createDevicePageCollector } from '@/services/devicePages';
import { isAppHome } from '@/services/deviceService';
import { useEffect, useState, useCallback, useRef } from 'react';
import { gUtils } from '@/utils/gUtils';
import { gConfig } from '@constants/gConfig';
import { ACTION_TYPES } from '@constants/messageConstants';
import { useWebSocket, sendMessage } from '@hooks/useWebSocket.ts';
import { useCallbacks } from '../hooks/useCallbacks.js';
import WebSocketManager, { WS_STATUS } from '@/websocket/WebSocketManager.ts';

const PubedCompanyDevice = 'PubedCompanyDevice';
const PubedUserDevice = 'PubedUserDevice';
export const useManageDevice = (gAuth, gStripe, setSnackbarValue) => {
  const [filteredSsmDevices, setFilteredSsmDevices] = useState([]); //公司用户ssm设备
  const [filteredAccessControlDevices, setFilteredAccessControlDevices] = useState([]); //公司用户认证机器
  const [canChoosedAccessControlDevices, setCanChoosedAccessControlDevices] = useState([]); //用户可选择的认证机器
  const [canChoosedSsmDevices, setCanChoosedSsmDevices] = useState([]); //用户可选择的ssm设备
  const [userDevices, setUserDevices] = useState([]); //个人用户所有设备
  const [devicesLoaded, setDevicesLoaded] = useState(false);
  const [companyDevices, setCompanyDevices] = useState([]); //公司用户所有设备
  const [deviceStatus, setDeviceStatus] = useState(null); //单个设备详情
  const { registerCallback, invokeCallbacks } = useCallbacks();
  // A completed save may arrive after sign-out, an account switch, or unmount.
  const orderScope = useRef({ key: null, revision: 0 });
  const orderScopeKey = JSON.stringify([
    gAuth.loginState,
    gStripe.customerInfo.subUUID,
    gStripe.customerInfo.companyID,
    gStripe.customerInfo.isAnonymous,
  ]);
  if (orderScope.current.key !== orderScopeKey) {
    orderScope.current.key = orderScopeKey;
    orderScope.current.revision++;
  }
  useEffect(
    () => () => {
      orderScope.current.revision++;
    },
    []
  );

  const companyPages = useRef(createDevicePageCollector());
  const userPages = useRef(createDevicePageCollector());

  const handleManageDeviceResponse = useCallback(
    (message) => {
      if (message.action !== ACTION_TYPES.BIZ3_MANAGE_DEVICE) return;
      invokeCallbacks(message);
      if (!message.success) {
        if (message.message === 'Limit Exceeded') {
          setSnackbarValue({ open: true, msg: 'デバイス数の上限に達しました。プランのアップグレードが必要です。' });
        } else {
          setSnackbarValue({
            logScope: 'api/useManageDevice.handleManageDeviceResponse',
            severity: 'error',
            open: true,
            msg: message.message,
          });
        }
        return;
      }
      switch (message.op) {
        case PubedCompanyDevice:
        case PubedUserDevice:
          const {
            totalPage,
            data: { list, page },
          } = message.data;
          const collector = message.op === PubedCompanyDevice ? companyPages : userPages;
          const complete = collector.current({ list, page, totalPage });
          if (complete !== null) {
            if (message.op === PubedCompanyDevice) {
              setCompanyDevices(complete);
              setDevicesLoaded(true);
            } else {
              setUserDevices(complete);
            }
          }
          break;
        case 'add':
          getCompanyDevices();
          break;
        case 'del':
          getCompanyDevices();
          break;
        case 'updateName':
          const context = message.reqContext;
          if (context && context.deviceUUID) {
            updateDeviceState({ deviceUUID: context.deviceUUID, deviceName: context.deviceName });
          }
          break;
        case 'getDeviceStatus':
          setDeviceStatus(message.data?.length > 0 ? message.data[0] : null);
          break;
        default:
          break;
      }
    },
    [gAuth, gStripe]
  );

  useWebSocket(ACTION_TYPES.BIZ3_MANAGE_DEVICE, handleManageDeviceResponse);

  useEffect(() => {
    if (!isAppHome) return;
    return WebSocketManager.onStatusChange((status) => {
      if (status === WS_STATUS.DISCONNECTED) {
        setCompanyDevices((devices) =>
          devices.map((device) => ({ ...device, stateInfo: { ...device.stateInfo, wm2State: false } }))
        );
      }
    });
  }, []);

  // 发送消息给 Hub3WebSocket
  const handleSendMessage = (message) => {
    if (gStripe.isFromApp && !isAppHome) {
      return;
    }
    sendMessage(message);
  };

  useEffect(() => {
    if (gAuth.loginState === gConfig.loginState.loginOut) {
      setFilteredSsmDevices([]);
      setFilteredAccessControlDevices([]);
      setCanChoosedAccessControlDevices([]);
      setCanChoosedSsmDevices([]);
      setUserDevices([]);
      setCompanyDevices([]);
      setDevicesLoaded(false);
      setDeviceStatus(null);
    }
  }, [gAuth.loginState]);

  useEffect(() => {
    const willDisplayedSsmDevices = companyDevices.filter((item) => {
      return gUtils.isLockModel(item.deviceModel);
    });
    setFilteredSsmDevices(willDisplayedSsmDevices);
    const willDisplayedAcDevices = companyDevices.filter((item) => {
      return gUtils.isSesameAccessControlDevice(item.deviceModel);
    });
    setFilteredAccessControlDevices(willDisplayedAcDevices);
  }, [companyDevices]);

  useEffect(() => {
    setCanChoosedAccessControlDevices(
      userDevices.filter((item) => {
        if (gUtils.isSesameAccessControlDevice(item.deviceModel) && gUtils.isDeviceKeyOwner(item.keyLevel)) {
          return !filteredAccessControlDevices.some((it) => it.deviceUUID === item.deviceUUID);
        }
        return false;
      })
    );
    setCanChoosedSsmDevices(
      userDevices.filter((item) => {
        if (gUtils.isLockModel(item.deviceModel) && gUtils.isDeviceKeyOwner(item.keyLevel)) {
          return !filteredSsmDevices.some((it) => it.deviceUUID === item.deviceUUID);
        }
        return false;
      })
    );
  }, [userDevices, filteredAccessControlDevices, filteredSsmDevices]);

  const updateDeviceState = useCallback((updatedDevice) => {
    const mergeStateInfo = (device) =>
      device?.deviceUUID === updatedDevice.deviceUUID
        ? {
            ...device,
            ...updatedDevice,
            stateInfo: updatedDevice.stateInfo
              ? { ...(device.stateInfo || {}), ...updatedDevice.stateInfo }
              : device.stateInfo,
          }
        : device;
    setCompanyDevices((prevDevices) => prevDevices.map(mergeStateInfo));
    setDeviceStatus((prevDevice) => mergeStateInfo(prevDevice));
  }, []);

  useEffect(() => {
    if (!gStripe.customerInfo.companyID) {
      return;
    }
    if (isAppHome) {
      setDevicesLoaded(false);
      setCompanyDevices([]);
      companyPages.current = createDevicePageCollector();
    }
    getCompanyDevices();
    if (!gStripe.customerInfo.isSesameApp) {
      getUserDevices();
    }
  }, [gStripe.customerInfo.companyID, gStripe.customerInfo.subUUID]);

  const getWifiState = useCallback(
    (touchId) => {
      let result = false;

      // 检查 filteredAccessControlDevices 是否为有效数组
      if (!Array.isArray(filteredAccessControlDevices)) {
        console.error('filteredAccessControlDevices is not a valid array');
        return result;
      }

      // 查找匹配的设备
      const matchedDevice = filteredAccessControlDevices.find((item) => item.deviceUUID === touchId);

      // 检查 stateInfo 和 wm2State 是否存在
      if (matchedDevice && matchedDevice.stateInfo && matchedDevice.stateInfo.wm2State !== undefined) {
        result = matchedDevice.stateInfo.wm2State;
      } else {
        result = false;
      }

      if (!result) {
        setSnackbarValue({ open: true, msg: 'Hub3との通信状況をご確認ください' });
      }

      return result;
    },
    [filteredAccessControlDevices, setSnackbarValue]
  );

  const findTouchName = useCallback(
    (touchId) => {
      return filteredAccessControlDevices.find((item) => item.deviceUUID === touchId)?.deviceName ?? touchId;
    },
    [filteredAccessControlDevices]
  );

  const getUserDevices = useCallback(
    (forceFetch = false) => {
      let message = { action: ACTION_TYPES.BIZ3_MANAGE_DEVICE, op: 'getUserDevice' };
      forceFetch ? sendMessage(message) : handleSendMessage(message);
    },
    [handleSendMessage]
  );

  const getCompanyDevices = useCallback(
    (forceFetch = false) => {
      const companyID = gStripe.customerInfo.companyID;
      if (!companyID) return;
      let message = { action: ACTION_TYPES.BIZ3_MANAGE_DEVICE, op: 'getCompanyDevice', companyID };
      forceFetch ? sendMessage(message) : handleSendMessage(message);
    },
    [handleSendMessage, gStripe.customerInfo.companyID]
  );

  const removeSesameDevices = useCallback(
    (items, cb, forceFetch = false) => {
      const companyID = gStripe.customerInfo.companyID;
      if (!companyID) return;
      let message = { action: ACTION_TYPES.BIZ3_MANAGE_DEVICE, op: 'del', companyID, items };
      forceFetch ? sendMessage(message) : handleSendMessage(message);
      registerCallback(message.action, message.op, cb);
    },
    [gStripe.customerInfo.companyID, handleSendMessage, registerCallback]
  );

  const updateDeviceName = useCallback(
    ({ subUUID, deviceUUID, deviceName }, cb) => {
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_DEVICE,
        obj: {
          subUUID,
          deviceUUID,
          deviceName,
        },
        op: 'updateName',
      };
      sendMessage(messageData);
      registerCallback(messageData.action, messageData.op, cb);
    },
    [registerCallback]
  );

  const addSesameDevicesToBiz3 = useCallback(
    (items, cb) => {
      const message = {
        action: ACTION_TYPES.BIZ3_MANAGE_DEVICE,
        op: 'add',
        items,
        companyID: gStripe.customerInfo.companyID,
      };
      handleSendMessage(message);
      registerCallback(message.action, message.op, cb);
    },
    [gStripe.customerInfo.companyID, handleSendMessage, registerCallback]
  );

  // Hub3 LTE 继电器使能：把 enable1/enable2 写入后台 hub3_status.relay_info
  // 只传需要改动的那一路即可（enable1 或 enable2）
  const updateRelayEnable = useCallback(
    ({ deviceUUID, enable1, enable2 }, cb) => {
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_DEVICE,
        obj: {
          deviceUUID,
          ...(enable1 !== undefined && { enable1 }),
          ...(enable2 !== undefined && { enable2 }),
        },
        op: 'updateRelayEnable',
      };
      sendMessage(messageData);
      registerCallback(messageData.action, messageData.op, cb);
    },
    [registerCallback]
  );

  const updateDeviceOrderKey = useCallback(
    (obj, cb, orderedIds) => {
      const revision = orderScope.current.revision;
      const sessionRevision = appSessionRevision();
      const message = {
        action: ACTION_TYPES.BIZ3_MANAGE_DEVICE,
        obj,
        op: 'updateDeviceOrderKey',
      };
      handleSendMessage(message);
      registerCallback(message.action, message.op, (response) => {
        if (revision !== orderScope.current.revision || sessionRevision !== appSessionRevision()) return;
        if (response.success && orderedIds) {
          const positions = new Map(orderedIds.map((id, index) => [id, index]));
          setCompanyDevices((devices) =>
            revision !== orderScope.current.revision || sessionRevision !== appSessionRevision()
              ? devices
              : [...devices].sort(
                  (a, b) => (positions.get(a.deviceUUID) ?? Infinity) - (positions.get(b.deviceUUID) ?? Infinity)
                )
          );
          // Fetch the server-generated order keys for the next move. The ordered
          // shared list also flows through the existing native persistence path.
          getCompanyDevices();
        }
        cb?.(response);
      });
    },
    [gStripe.customerInfo.companyID, handleSendMessage, registerCallback, getCompanyDevices]
  );

  const getDevicesNotifyStatus = useCallback(
    ({ pushToken, items }, cb) => {
      let companyID = gStripe.customerInfo.companyID;
      if (!companyID) return;
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_DEVICE,
        companyID,
        pushToken,
        items,
        op: 'notifyList',
      };
      sendMessage(messageData);
      registerCallback(messageData.action, messageData.op, cb);
    },
    [gStripe.customerInfo.companyID, registerCallback]
  );

  const switchDeviceNotify = useCallback(
    ({ pushToken, deviceUUID, enablePush }, cb) => {
      let companyID = gStripe.customerInfo.companyID;
      if (!companyID) return;
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_DEVICE,
        companyID,
        enablePush,
        deviceUUID,
        pushToken,
        op: 'notifyManage',
      };
      sendMessage(messageData);
      registerCallback(messageData.action, messageData.op, cb);
    },
    [gStripe.customerInfo.companyID, registerCallback]
  );

  // 设备状态订阅由服务端在 getCompanyDevice / getDeviceStatus 时自动完成；重连后重新拉取即重新订阅
  useEffect(() => {
    const unsubscribe = WebSocketManager.onConnectionIdChange((newConnectionId) => {
      console.log('【Device】检测到 WebSocket connectionId 变化:', newConnectionId);
      getCompanyDevices();
    });
    return () => unsubscribe();
  }, [getCompanyDevices]);

  const switchRechargebleBattery = useCallback(
    ({ deviceUUID, isRechargeBattery }, cb) => {
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_DEVICE,
        deviceUUID,
        isRechargeBattery: isRechargeBattery ? 1 : 0,
        op: 'switchRecharge',
      };
      sendMessage(messageData);
      registerCallback(messageData.action, messageData.op, cb);
    },
    [registerCallback]
  );

  const getDeviceStatus = useCallback(
    (deviceUUID) => {
      if (!deviceUUID) return;
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_DEVICE,
        deviceUUID,
        op: 'getDeviceStatus',
      };
      sendMessage(messageData);
    },
    [sendMessage, gStripe.isFromApp]
  );

  const getFactoryInfo = useCallback(
    (deviceUUID, cb) => {
      if (!deviceUUID) return;
      const messageData = {
        action: ACTION_TYPES.BIZ3_MANAGE_DEVICE,
        deviceUUID,
        op: 'getFactoryInfo',
      };
      sendMessage(messageData);
      registerCallback(messageData.action, messageData.op, cb);
    },
    [registerCallback]
  );

  return {
    companyDevices,
    devicesLoaded,
    getCompanyDevices,
    filteredAccessControlDevices,
    setFilteredAccessControlDevices,
    userDevices,
    getUserDevices,
    canChoosedAccessControlDevices,
    canChoosedSsmDevices,
    removeSesameDevices, //移除设备
    addSesameDevicesToBiz3, //添加锁
    filteredSsmDevices, //ssm设备
    getWifiState,
    findTouchName,
    updateDeviceName,
    updateRelayEnable,
    updateDeviceState,
    updateDeviceOrderKey,
    getDevicesNotifyStatus,
    switchDeviceNotify,
    switchRechargebleBattery,
    deviceStatus,
    getDeviceStatus,
    getFactoryInfo,
  };
};
